/**
 * SkuLift Logo (paramétrique SVG)
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-005
 *
 * Variants:
 *   - mark            : just the stylized "S" glyph (1:1 ratio)
 *   - wordmark        : S + "SkuLift" wordmark (~4:1 ratio)
 *   - wordmark-tagline: + tagline below (~4:1.5 ratio)
 *
 * Themes:
 *   - dark : cyan glyph + white wordmark + light-gray tagline (default — for ink backgrounds)
 *   - light: cyan glyph + ink-950 wordmark + ink-900 tagline (for white/light backgrounds)
 */

import { cn } from '@/lib/utils';

type LogoVariant = 'mark' | 'wordmark' | 'wordmark-tagline';
type LogoTheme = 'dark' | 'light';

export interface LogoProps {
  variant?: LogoVariant;
  theme?: LogoTheme;
  /** Height in rem (em-based scaling). Width derives from variant ratio. */
  size?: number;
  className?: string;
  /** Locale-aware tagline (used for `wordmark-tagline`). Default FR. */
  tagline?: string;
  /** ARIA label fallback (used when no children). */
  label?: string;
}

const TAGLINE_FR = 'Devenez la réponse citée par les IA';
const TAGLINE_EN = 'Become the answer AI engines cite';

const COLORS = {
  dark: {
    glyph: '#1aabff',
    wordmark: '#ffffff',
    tagline: '#eeeeee',
  },
  light: {
    glyph: '#1aabff',
    wordmark: '#0a0f1f',
    tagline: '#1a2034',
  },
} as const;

/**
 * Stylized "S" glyph — Comfortaa-derived, single-stroke cyan curve in a 64x64 viewport.
 */
function Glyph({ color }: { color: string }) {
  return (
    <g>
      {/* Outer rounded square frame */}
      <rect
        x="2"
        y="2"
        width="60"
        height="60"
        rx="14"
        ry="14"
        fill="none"
        stroke={color}
        strokeWidth="3"
      />
      {/* The "S" curve — two interlocking quarter arcs */}
      <path
        d="M46 20
           Q46 14 38 14
           L26 14
           Q18 14 18 22
           Q18 30 26 30
           L38 30
           Q46 30 46 38
           Q46 46 38 46
           L26 46
           Q18 46 18 40"
        fill="none"
        stroke={color}
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

export function Logo({
  variant = 'mark',
  theme = 'dark',
  size = 2,
  className,
  tagline,
  label = 'SkuLift',
}: LogoProps) {
  const palette = COLORS[theme];
  const heightRem = `${size}rem`;
  const taglineText = tagline ?? TAGLINE_FR;

  if (variant === 'mark') {
    return (
      <svg
        viewBox="0 0 64 64"
        role="img"
        aria-label={label}
        className={cn('inline-block align-middle', className)}
        style={{ height: heightRem, width: heightRem }}
      >
        <Glyph color={palette.glyph} />
      </svg>
    );
  }

  if (variant === 'wordmark') {
    return (
      <svg
        viewBox="0 0 256 64"
        role="img"
        aria-label={label}
        className={cn('inline-block align-middle', className)}
        style={{ height: heightRem, width: `${size * 4}rem` }}
      >
        <Glyph color={palette.glyph} />
        <text
          x="76"
          y="44"
          fill={palette.wordmark}
          fontFamily="var(--font-comfortaa), Comfortaa, sans-serif"
          fontWeight="700"
          fontSize="34"
          letterSpacing="-0.02em"
        >
          SkuLift
        </text>
      </svg>
    );
  }

  // wordmark-tagline
  return (
    <svg
      viewBox="0 0 360 96"
      role="img"
      aria-label={`${label} — ${taglineText}`}
      className={cn('inline-block align-middle', className)}
      style={{ height: `${size * 1.5}rem`, width: `${size * 5.6}rem` }}
    >
      <Glyph color={palette.glyph} />
      <text
        x="76"
        y="40"
        fill={palette.wordmark}
        fontFamily="var(--font-comfortaa), Comfortaa, sans-serif"
        fontWeight="700"
        fontSize="32"
        letterSpacing="-0.02em"
      >
        SkuLift
      </text>
      <text
        x="76"
        y="68"
        fill={palette.tagline}
        fontFamily="var(--font-inter), Inter, sans-serif"
        fontWeight="400"
        fontSize="12"
        opacity="0.85"
      >
        {taglineText}
      </text>
    </svg>
  );
}

export { TAGLINE_FR, TAGLINE_EN };
